import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import DataTable from '../components/ui/DataTable';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Faq from '../components/ui/Faq';
import Button from '../components/ui/Button';
import { serviceSchema, faqSchema, breadcrumbSchema } from '../components/ui/schema';

const PropertyDevelopment = () => {
  const { t } = useTranslation('cy');
  const meta = t('property.meta', { returnObjects: true });

  const faqItems = t('property.faq.items', { returnObjects: true });

  const assessmentRows = t('property.assessment.rows', { returnObjects: true }).map((r) => ({
    face: r.face,
    input: r.input,
    evidence:
      typeof r.evidence === 'string' ? (
        r.evidence
      ) : (
        <>
          <strong style={{ color: 'var(--cy-color-text-primary)', fontWeight: 600 }}>{r.evidence.lead}</strong>
          {' \u2014 '}
          {r.evidence.rest}
        </>
      ),
    impact: r.impact,
  }));

  const schema = [
    serviceSchema('Property Development', meta.description),
    faqSchema(faqItems),
    breadcrumbSchema([{ label: 'Home', to: '/' }, { label: 'Property Development', to: '/property-development' }]),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/property-development" schema={schema} />

      <PageHero
        id="property-hero"
        eyebrow={t('property.hero.eyebrow')}
        title={t('property.hero.h1')}
        lead={t('property.hero.lead')}
        breadcrumb={
          <Breadcrumbs items={[{ label: t('nav.pillars.property.label') }]} />
        }
      >
        <p className="cy-body" style={{ marginTop: 24 }}>{t('property.hero.boundary')}</p>
        <HeroCtas
          primary={t('property.hero.cta')}
          primaryTo="/submit-opportunity?type=property"
          secondary={t('property.hero.seeCriteria')}
          secondaryTo="#property-fit"
        />
        <Notice variant="inline" className="cy-hero__boundary">
          {t('property.hero.roleNote')}{' '}
          <Link className="cy-link" to="/terms#terms-investment-boundary">Terms &amp; Conditions</Link>
        </Notice>
      </PageHero>

      {/* property-fit */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="property-fit">
        <div className="cy-container">
          <SectionHead title={t('property.fit.h2')} lead={t('property.fit.lead')} />
          <DataTable
            columns={[
              { key: 'dim', label: 'Dimension' },
              { key: 'what', label: 'What we look at' },
              { key: 'why', label: 'Why' },
            ]}
            rows={t('property.fit.rows', { returnObjects: true })}
          />
          <p className="cy-body" style={{ marginTop: 24 }}>
            {t('property.fit.note')}{' '}
            <Link className="cy-link" to="#property-materials">prepare to submit</Link>
          </p>
        </div>
      </section>

      {/* property-not-fit */}
      <section className="cy-section cy-zone--doc" id="property-not-fit">
        <div className="cy-container">
          <SectionHead title={t('property.notFit.h2')} lead={t('property.notFit.lead')} />
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {t('property.notFit.items', { returnObjects: true }).map((item) => (
              <li className="cy-principle" key={item.title}>
                <h3 className="cy-principle__title">{item.title}</h3>
                <p className="cy-card-text">{item.body}</p>
              </li>
            ))}
          </ul>
          <Notice variant="compliance" className="cy-notice--inline" style={{ marginTop: 24 }}>
            {t('property.notFit.boundary')}
          </Notice>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/contact">{t('property.notFit.alt')}</Link>
          </p>
        </div>
      </section>

      {/* property-roles */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="property-roles">
        <div className="cy-container">
          <SectionHead title={t('property.roles.h2')} lead={t('property.roles.lead')} />
          <DataTable
            columns={[
              { key: 'role', label: 'Role' },
              { key: 'when', label: 'Applies when' },
              { key: 'responsibility', label: 'Responsibility' },
              { key: 'notMeaning', label: 'Does not mean' },
            ]}
            rows={t('property.roles.rows', { returnObjects: true })}
          />
          <Notice variant="compliance" style={{ marginTop: 24 }}>
            {t('property.roles.note')}{' '}
            <Link className="cy-link" to="/approach#approach-roles">How roles are defined</Link>
          </Notice>
        </div>
      </section>

      {/* property-assessment */}
      <section className="cy-section cy-zone--doc" id="property-assessment">
        <div className="cy-container">
          <SectionHead eyebrow={t('property.assessment.eyebrow')} title={t('property.assessment.h2')} lead={t('property.assessment.lead')} />
          <DataTable
            columns={[
              { key: 'face', label: 'Lens' },
              { key: 'input', label: 'Input needed' },
              { key: 'evidence', label: 'Evidence required' },
              { key: 'impact', label: 'Impact on decision' },
            ]}
            rows={assessmentRows}
          />
          <p className="cy-body" style={{ marginTop: 24 }}>
            {t('property.assessment.lens')}
          </p>
          <p className="cy-fineprint" style={{ marginTop: 12 }}>
            {t('property.assessment.gateNote')}{' '}
            <Link className="cy-link" to="/approach#approach-gates">{t('property.assessment.gateLink')}</Link>
          </p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-stages">{t('property.assessment.cta')}</Link>
          </p>
        </div>
      </section>

      {/* property-materials */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="property-materials">
        <div className="cy-container">
          <SectionHead title={t('property.materials.h2')} lead={t('property.materials.lead')} />
          <div className="cy-grid cy-grid--2">
            <div className="cy-card">
              <h3 className="cy-card-title">Minimum to include</h3>
              <ul className="cy-checklist">
                {t('property.materials.minimum', { returnObjects: true }).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="cy-card">
              <h3 className="cy-card-title">Can follow later</h3>
              <ul className="cy-checklist">
                {t('property.materials.later', { returnObjects: true }).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <Notice variant="inline" style={{ marginTop: 24 }}>
            {t('property.materials.privacy')}{' '}
            <Link className="cy-link" to="/privacy#privacy-collection">Privacy Policy</Link>
          </Notice>
          <div style={{ marginTop: 32 }}>
            <Button to="/submit-opportunity?type=property">{t('property.materials.cta')}</Button>
          </div>
        </div>
      </section>

      {/* property-intermediaries */}
      <section className="cy-section cy-zone--doc" id="property-intermediaries">
        <div className="cy-container">
          <SectionHead title={t('property.intermediaries.h2')} lead={t('property.intermediaries.lead')} />
          <DataTable
            columns={[
              { key: 'point', label: 'What we ask' },
              { key: 'detail', label: 'Our position' },
              { key: 'why', label: 'Why it matters' },
            ]}
            rows={t('property.intermediaries.rows', { returnObjects: true })}
          />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('property.intermediaries.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-risk">{t('property.intermediaries.cta')}</Link>
          </p>
        </div>
      </section>

      {/* property-path */}
      <section className="cy-section cy-section--alt cy-zone--doc" id="property-path">
        <div className="cy-container">
          <SectionHead title={t('property.path.h2')} lead={t('property.path.lead')} />
          <StageList items={t('property.path.steps', { returnObjects: true })} />
          <p className="cy-fineprint" style={{ marginTop: 24 }}>{t('property.path.note')}</p>
          <p style={{ marginTop: 16 }}>
            <Link className="cy-link" to="/approach#approach-stages">{t('property.path.cta')}</Link>
          </p>
        </div>
      </section>

      {/* property-faq-cta */}
      <section className="cy-section cy-section--cta" id="property-faq-cta">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('property.faq.h2')} />
          <Faq items={faqItems} idPrefix="property-faq" />
          <div style={{ marginTop: 48, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Button to="/submit-opportunity?type=property">{t('property.faq.cta')}</Button>
            <Link className="cy-link" to="/contact">{t('property.faq.alt')}</Link>
          </div>
          <p className="cy-fineprint" style={{ marginTop: 32 }}>
            {t('property.faq.siblings', { returnObjects: true }).map((s, i, arr) => {
              const to =
                s === 'Built Environment Investments' ? '/built-environment-investments' : '/innovation-ventures';
              return (
                <React.Fragment key={s}>
                  <Link className="cy-link" to={to}>{s}</Link>
                  {i < arr.length - 1 ? ' · ' : ''}
                </React.Fragment>
              );
            })}
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default PropertyDevelopment;
